"use client"

import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import * as React from "react"
import { useTheme } from "next-themes"
import { Direction } from "radix-ui"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"

/** The gap between the toasts and their edge of the screen (sonner's own default, kept). */
const EDGE_OFFSET = 24
/** sonner's toast width (TOAST_WIDTH, sonner 2.x — the list's `--width`). */
const SONNER_TOAST_WIDTH = 356

/**
 * STG-751 — how far the toasts stand off their edge. The toasts sit at the top of
 * the text's END edge (right in English, left in Arabic) at a z-index above
 * everything, so an open drawer on that edge — the scenario preview drawer in the
 * report — had its top controls painted over. While a sheet is open on that edge
 * the toasts move to its inner edge; a sheet too wide to stand beside keeps them
 * on screen instead. A sheet on the other edge, or none, leaves EDGE_OFFSET.
 *
 * Sheets are read from the DOM (components/ui/sheet.tsx sets `data-slot`,
 * `data-side` and Radix's `data-state`) because their open state lives in each
 * sheet's own Radix context; sheet.tsx watches the same attribute to make a
 * closing sheet inert. A sheet is portaled straight into <body>, so its mount is
 * a child of body and its open/close is a `data-state` change beneath it.
 */
function useSideOffset(rtl: boolean): number {
  const [side, setSide] = React.useState(EDGE_OFFSET)
  React.useEffect(() => {
    // "end" follows the text direction; "left"/"right" name a physical edge.
    const onToastEdge = new Set(["end", rtl ? "left" : "right"])
    let frame = 0
    const measure = () => {
      frame = 0
      let drawer = 0
      document
        .querySelectorAll<HTMLElement>('[data-slot="sheet-content"][data-state="open"]')
        .forEach((sheet) => {
          if (onToastEdge.has(sheet.getAttribute("data-side") ?? "")) {
            drawer = Math.max(drawer, sheet.getBoundingClientRect().width)
          }
        })
      const beside = EDGE_OFFSET + Math.round(drawer)
      const lastOnScreen = window.innerWidth - SONNER_TOAST_WIDTH - EDGE_OFFSET
      setSide(Math.max(EDGE_OFFSET, Math.min(beside, lastOnScreen)))
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    measure()
    const mounts = new MutationObserver(schedule)
    mounts.observe(document.body, { childList: true })
    const states = new MutationObserver(schedule)
    states.observe(document.body, { subtree: true, attributes: true, attributeFilter: ["data-state"] })
    return () => {
      mounts.disconnect()
      states.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [rtl])
  return side
}

const Toaster = ({ ...props }: ToasterProps) => {
  // sonner paints part of a toast from its OWN theme (the description is #3f3f3f in light,
  // #e8e8e8 in dark), so it must get the mode the page is really in. In the admin console that
  // is the admin's own mode, which components/theme-provider.tsx passes next-themes as
  // `forcedTheme` — set there and nowhere else, and only while a console page is open. Everywhere
  // else `forcedTheme` is undefined and this is `theme`, exactly as before
  // (components/ui/__tests__/sonner-console-mode.spec.tsx).
  const { theme = "system", forcedTheme } = useTheme()
  // sonner reads the document direction once, at mount; a language switch
  // afterwards would leave it behind, so it is passed in (RTL-05).
  const rtl = Direction.useDirection() === "rtl"
  const sideOffset = useSideOffset(rtl)
  // The toast region's accessible name, in the interface language (browser QA A-9).
  const { t } = useShellTranslation()
  const [mounted, setMounted] = React.useState(false)

  React.useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null

  return (
    <Sonner
      theme={(forcedTheme ?? theme) as ToasterProps["theme"]}
      className="toaster group"
      containerAriaLabel={t("shell.toaster.notifications")}
      // #445 — DISABLE sonner's expand hotkey.
      //
      // Its default is ['altKey','KeyT']. One press sets expanded=true on the
      // Toaster, and every toast's timer effect does
      // `if (expanded || interacting || isDocumentHidden) pauseTimer()` — so a
      // single keystroke freezes the auto-close timer of EVERY toast at once.
      // This Toaster is mounted in the root layout, above the router, so the
      // latch survives client-side navigation: a "Recipient moved to recycle
      // bin" toast was measured still on screen 60 seconds later, after
      // browsing through Groups and Import.
      //
      // NOT a duration problem. sonner's TOAST_LIFETIME is already 4000ms and
      // the un-pressed baseline measured 4000ms exactly. A paused timer is not
      // a short timer.
      //
      // NEVER write `hotkey={[]}`. `[].every(...)` returns true, so an empty
      // array makes sonner's isHotkeyPressed true for EVERY keydown — that
      // turns "needs Alt+T" into "any keystroke". Require a combination a
      // keyboard cannot produce instead.
      //
      // Cost, accepted: the live-region aria-label becomes
      // "Notifications alt+ctrl+shift+meta+F13" (sonner builds it from this
      // prop and offers no override), and sonner's keyboard route into the
      // toast list is gone. Toasts remain Tab-reachable — the <li> keeps
      // tabIndex 0 — so nothing becomes unreachable.
      hotkey={["altKey", "ctrlKey", "shiftKey", "metaKey", "F13"]}
      // Top-right so toasts never overlap bottom-right action buttons
      // (e.g. the campaign-wizard "Next" button — that collision is WHY this
      // is top-right and not bottom-right; do not flip it back).
      //
      // #299 (2026-08-12): 80px only clears the 64px (h-16) sticky app-shell
      // header itself. Every PageShell page puts its own action row (Import
      // CSV / Add Recipient, etc.) immediately below that header — often with
      // as little as 16px of body padding (`p-4`, some pages `pt-0`) — so
      // that row's buttons START at y≈64-80 and typically run another
      // ~36-40px (a default-size Button). A toast starting AT y=80 sits
      // exactly on top of that row, and Sonner's portal wins hit-testing, so
      // the buttons underneath go dead for as long as a toast is on screen.
      // 140px clears the header + the tightest observed body padding + a
      // full action-row button height with margin, on every page audited
      // (recipients, phishing/campaigns, phishing/library/emails,
      // recipients/getting-started, company-dashboard — spanning `p-4`,
      // `p-4 pt-0`, and `p-6`). Callers can override via `{...props}`.
      //
      // Right-to-left mirrors the page, action buttons included, so the
      // toasts take the mirrored corner: top-left.
      //
      // The side offset is 24 unless an open drawer is on that edge (STG-751,
      // useSideOffset above).
      dir={rtl ? "rtl" : "ltr"}
      position={rtl ? "top-left" : "top-right"}
      offset={rtl ? { top: 140, left: sideOffset } : { top: 140, right: sideOffset }}
      closeButton
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
          // The close button on the toast's far top corner. sonner applies
          // these as physical left/right, so right-to-left swaps them.
          "--toast-close-button-start": rtl ? "0" : "auto",
          "--toast-close-button-end": rtl ? "auto" : "0",
          "--toast-close-button-transform": rtl ? "translate(-35%, -35%)" : "translate(35%, -35%)",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
